// This needs to be imported first to avoid order-of-dependency issues with
// imports.
// eslint-disable-next-line import/order
import { configureStore } from '@app/store'

import { ConnectedRouter } from 'connected-react-router'
import { useEffect } from 'react'
import * as React from 'react'
import '@patternfly/react-core/dist/styles/base.css'
import '@app/app.css'
import { Provider, useDispatch, useSelector, connect } from 'react-redux'
import { Redirect, Route, Switch } from 'react-router-dom'

import { Role } from '@app/domain/iam/user.interface'
import * as Storage from '@app/lib/storage'
import history from '@app/routing/history'
import { RequireRole } from '@app/routing/require-role'
import { RouteAuthed } from '@app/routing/route-authed'
import { RouteUnauthed } from '@app/routing/route-unauthed'
import AccountScreen from '@app/screens/account'
import AdminScreen from '@app/screens/admin'
import Clinic from '@app/screens/clinic/clinic'
import IntakeScreen from '@app/screens/intake'
import { ForgotPasswordScreen as ForgotPassword } from '@app/screens/login/forgot-password'
import Login from '@app/screens/login/login'
import Logout from '@app/screens/login/logout'
import { ResetPasswordScreen as ResetPassword } from '@app/screens/login/reset-password'
import type { AppState } from '@app/state'
import { queries } from '@app/state/authenticate/state'
import * as FeatureFlags from '@app/state/feature-flags/state'

import { NotFoundScreen } from './screens/404'
import { AdminPatientsScreen } from './screens/admin/patients'

import type { ConnectedProps } from 'react-redux'

Storage.initialize(window.localStorage, window.sessionStorage)

const store = configureStore(history)

const App: React.FunctionComponent = () => (
  <Provider store={store}>
    <ConnectedIntake />
  </Provider>
)

const mapState = (state: AppState) => ({
  booted: state.init.hasBooted,
})

const connector = connect(mapState)
type PropsFromRedux = ConnectedProps<typeof connector>

const Intake: React.FunctionComponent<PropsFromRedux> = (props) => {
  const dispatch = useDispatch()

  const isAuthenticated = useSelector<AppState, boolean>((state) => {
    return queries.isAuthenticated(state.auth)
  })

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(FeatureFlags.actions.subscribeToFeatureFlags())
    }
  }, [dispatch, isAuthenticated])

  if (props.booted) {
    return (
      <ConnectedRouter history={history}>
        <Switch>
          <Route exact path="/">
            <RouteAuthed authedRedirectTo="/clinic/today">
              <Login />
            </RouteAuthed>
          </Route>
          <Route path="/logout" component={Logout} />
          <Route path="/forgot-password">
            <RouteAuthed authedRedirectTo="/clinic/today">
              <ForgotPassword />
            </RouteAuthed>
          </Route>
          <Route path="/reset-password">
            <RouteAuthed authedRedirectTo="/clinic/today">
              <ResetPassword />
            </RouteAuthed>
          </Route>
          <Route path="/clinic">
            <RouteUnauthed unauthedRedirectTo="/">
              <RequireRole role={Role.Admin} onFailRedirectTo="/logout">
                <Clinic />
              </RequireRole>
            </RouteUnauthed>
          </Route>
          <Route path="/account">
            <RouteUnauthed unauthedRedirectTo="/">
              <RequireRole role={Role.Admin} onFailRedirectTo="/logout">
                <AccountScreen />
              </RequireRole>
            </RouteUnauthed>
          </Route>
          <Route path="/intake">
            <RouteUnauthed unauthedRedirectTo="/">
              <IntakeScreen />
            </RouteUnauthed>
          </Route>
          <Route path="/admin/patients">
            <RouteUnauthed unauthedRedirectTo="/">
              <AdminPatientsScreen />
            </RouteUnauthed>
          </Route>
          <Route path="/admin">
            <RouteUnauthed unauthedRedirectTo="/">
              <AdminScreen />
            </RouteUnauthed>
          </Route>
          <Route path="/404">
            <NotFoundScreen />
          </Route>
          <Route path="*">
            <Redirect to="/404" />
          </Route>
        </Switch>
      </ConnectedRouter>
    )
  } else {
    return <></>
  }
}

const ConnectedIntake = connector(Intake)

export { App }
